﻿/**
 * Provider Marketplace â€” Phase 8
 *
 * Browse, install, and configure connector providers.
 * Supports one-click install for pre-built providers and custom provider creation.
 */

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter
} from '@retailsync/ui'
import { Button, Badge, Input } from '@retailsync/ui'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '@retailsync/ui'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@retailsync/ui'
import { Tabs, TabsList, TabsTrigger } from '@retailsync/ui'
import {
  ShoppingBagIcon,
  CalculatorIcon,
  CubeIcon,
  ServerIcon,
  TableCellsIcon,
  CodeBracketIcon,
  PuzzlePieceIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  CogIcon,
  XMarkIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'
import { API_ORIGIN, readApiResponse } from '../config/api'

// â”€â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface MarketplaceProvider {
  id: string
  name: string
  slug: string
  description: string
  icon: string
  category: string
  supportedEntities: string[]
  syncMethods: string[]
  authType: string
  configSchema: any
  isBuiltIn: boolean
  installCount: number
  rating: number
  version: string
  /**
   * Whether the runtime can actually drive this provider.
   *
   * False means we have no adapter for it (e.g. WooCommerce, Zoho, SFTP, Google
   * Sheets), so installing it would collect settings and then never sync
   * anything. Those are surfaced as "Planned" rather than presented as live
   * integrations.
   */
  adapterWired?: boolean
}

interface InstalledProvider {
  id: string
  providerId: string
  provider: MarketplaceProvider
  config: Record<string, any>
  isActive: boolean
  syncSchedule: string | null
  installedAt: string
  lastSyncAt: string | null
  syncStatus: string
}

const API_BASE = API_ORIGIN
async function apiCall<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('access_token') || ''
  const tenantId = localStorage.getItem('tenant_id') || ''
  const res = await fetch(`${API_BASE}/api/v1/connector${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenantId,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })
  // Reads the license payload for 402s instead of assuming the body is JSON data.
  const json = await readApiResponse<{ success: boolean; data: T; error?: string }>(res)
  if (!json.success) throw new Error(json.error || 'API error')
  return json.data
}

// â”€â”€â”€ Icon Map â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const ICON_MAP: Record<string, any> = {
  'square': PuzzlePieceIcon,
  'shopping-bag': ShoppingBagIcon,
  'wordpress': PuzzlePieceIcon,
  'calculator': CalculatorIcon,
  'package': CubeIcon,
  'code': CodeBracketIcon,
  'server': ServerIcon,
  'table-cells': TableCellsIcon,
  'puzzle': PuzzlePieceIcon,
}

function ProviderIcon({ icon }: { icon: string }) {
  const Icon = ICON_MAP[icon] || PuzzlePieceIcon
  return (
    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
      <Icon className="w-6 h-6 text-white" />
    </div>
  )
}

// â”€â”€â”€ Category filter â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'pos', label: 'POS Systems' },
  { value: 'ecommerce', label: 'E-Commerce' },
  { value: 'erp', label: 'ERP' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'crm', label: 'CRM' },
  { value: 'payment', label: 'Payments' },
  { value: 'other', label: 'Other' },
]

// â”€â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function ProviderMarketplace() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [providers, setProviders] = useState<MarketplaceProvider[]>([])
  const [installed, setInstalled] = useState<InstalledProvider[]>([])
  const [activeTab, setActiveTab] = useState<'marketplace' | 'installed'>('marketplace')
  const [category, setCategory] = useState('')
  const [search, setSearch] = useState('')
  const [selectedProvider, setSelectedProvider] = useState<MarketplaceProvider | null>(null)
  const [configForm, setConfigForm] = useState<Record<string, string>>({})
  const [installing, setInstalling] = useState(false)
  const [testing, setTesting] = useState<string | null>(null)
  const [installError, setInstallError] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (category) params.set('category', category)
      if (search) params.set('search', search)
      const [prov, inst] = await Promise.all([
        apiCall<MarketplaceProvider[]>(`/marketplace?${params.toString()}`),
        apiCall<InstalledProvider[]>('/marketplace/installed'),
      ])
      setProviders(prov)
      setInstalled(inst)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [category, search])

  useEffect(() => { loadData() }, [loadData])

  const isInstalled = (slug: string) => installed.some(i => i.providerId === slug)

  const openInstall = (provider: MarketplaceProvider) => {
    setSelectedProvider(provider)
    setConfigForm({})
    setInstallError(null)
  }

  const handleInstall = async () => {
    if (!selectedProvider) return
    setInstalling(true)
    setInstallError(null)
    try {
      await apiCall(`/marketplace/${selectedProvider.slug}/install`, {
        method: 'POST',
        body: JSON.stringify({ config: configForm, syncSchedule: '*/15 * * * *' }),
      })
      setSelectedProvider(null)
      await loadData()
      setActiveTab('installed')
    } catch (err: any) {
      setInstallError(err.message)
    } finally {
      setInstalling(false)
    }
  }

  const handleUninstall = async (slug: string) => {
    if (!confirm(`Uninstall ${slug}? This will stop all syncs for this provider.`)) return
    try {
      await apiCall(`/marketplace/${slug}/uninstall`, { method: 'DELETE' })
      await loadData()
    } catch (err: any) {
      alert(`Failed: ${err.message}`)
    }
  }

  const handleTestConnection = async (installedId: string) => {
    setTesting(installedId)
    try {
      const result = await apiCall<{ connected: boolean; details: any }>(
        `/marketplace/installed/${installedId}/test`,
        { method: 'POST' }
      )
      alert(result.connected ? 'Connection successful!' : `Connection failed: ${JSON.stringify(result.details)}`)
    } catch (err: any) {
      alert(`Test failed: ${err.message}`)
    } finally {
      setTesting(null)
    }
  }

  // â”€â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Provider Marketplace</h1>
        <p className="text-gray-500 mt-1">Connect your POS, ERP, and e-commerce systems</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as 'marketplace' | 'installed')}>
        <TabsList>
          <TabsTrigger value="marketplace">Discover</TabsTrigger>
          <TabsTrigger value="installed" className="flex items-center gap-2">
            Installed
            {installed.length > 0 && (
              <Badge variant="secondary" size="sm">{installed.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* â”€â”€â”€ Marketplace Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {activeTab === 'marketplace' && (
        <div className="space-y-6">
          {/* Featured: RetailsyncGo POS + Offline POS Bridge */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* RetailsyncGo POS */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-white/20 text-xs font-bold">FEATURED</span>
                  <span className="text-blue-200 text-xs">Native POS</span>
                </div>
                <h3 className="text-xl font-bold">RetailsyncGo POS</h3>
                <p className="text-blue-100 text-sm mt-1">
                  Our Electron desktop app. Auto-syncs at startup â€” zero data loss, works offline. Free with your account.
                </p>
                <div className="flex gap-2 mt-4">
                  <a href="https://retailsync.africa" target="_blank" rel="noopener"
                    className="px-4 py-2 bg-white text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors">
                    Download Free â†’
                  </a>
                  <Button variant="ghost" className="text-white hover:bg-white/10 text-sm"
                    onClick={() => openInstall(providers.find(p => p.slug === 'retailsync-pos')!)}>
                    Install Connector
                  </Button>
                </div>
              </div>
            </div>

            {/* Offline POS Bridge */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-white/20 text-xs font-bold">NO API NEEDED</span>
                  <span className="text-amber-100 text-xs">Manual Import</span>
                </div>
                <h3 className="text-xl font-bold">Offline POS Bridge</h3>
                <p className="text-amber-50 text-sm mt-1">
                  Stuck with a POS that can't connect? Export CSV, upload here, we auto-map & sync. Any POS, any format.
                </p>
                <div className="flex gap-2 mt-4">
                  <Link to="/offline-bridge"
                    className="px-4 py-2 bg-white text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-50 transition-colors inline-block">
                    Learn More â†’
                  </Link>
                  <Link to="/manual-sync"
                    className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm font-medium hover:bg-white/20 transition-colors border border-white/20 inline-block">
                    Manual Sync
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3 items-center">
            <div className="flex-1 max-w-sm">
              <Input
                type="text"
                placeholder="Search providers..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                icon={<MagnifyingGlassIcon className="w-4 h-4" />}
                aria-label="Search providers"
              />
            </div>
            <div className="w-52">
              <Select value={category} onValueChange={setCategory} placeholder="All Categories">
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Provider grid */}
          {error ? (
            <div className="text-center py-12 text-red-500">
              <ExclamationTriangleIcon className="w-12 h-12 mx-auto mb-3" />
              <p>{error}</p>
              <Button onClick={loadData} className="mt-3">Retry</Button>
            </div>
          ) : (
            <>
              {providers.some(p => p.adapterWired === false) && (
                <p className="text-xs text-gray-500">
                  Providers marked <Badge variant="warning">Planned</Badge> are on the roadmap — we
                  have no adapter for them yet, so they can’t sync data. Everything else can be
                  installed and synced today.
                </p>
              )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {providers.map(provider => (
                <Card key={provider.slug} className="hover:shadow-md transition-shadow flex flex-col">
                  <CardHeader className="flex-row items-start gap-3 space-y-0">
                    <ProviderIcon icon={provider.icon} />
                    <div className="flex-1 min-w-0">
                      <CardTitle className="truncate flex items-center gap-2">
                        {provider.name}
                        {/* Planned = catalogue entry with no runtime adapter. Installing it
                            would collect settings and never sync anything, so say so up front
                            instead of letting the user find out from an empty sync log. */}
                        {provider.adapterWired === false && (
                          <Badge variant="warning" className="shrink-0">Planned</Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-0.5">
                        v{provider.version} • {provider.installCount}+ installs
                        {provider.rating > 0 && ` • ★ ${provider.rating}`}
                      </CardDescription>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 space-y-3">
                    <p className="text-sm text-gray-600 line-clamp-2">{provider.description}</p>
                    {/* Entity tags */}
                    <div className="flex flex-wrap gap-1">
                      {provider.supportedEntities.map(e => (
                        <Badge key={e} variant="info">{e}</Badge>
                      ))}
                    </div>
                    {/* Sync methods */}
                    <div className="flex flex-wrap gap-1">
                      {provider.syncMethods.map(m => (
                        <Badge key={m} variant="default" className="capitalize">
                          {m}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                  <CardFooter>
                    {isInstalled(provider.slug) ? (
                      <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
                        <CheckCircleIcon className="w-4 h-4" /> Installed
                      </div>
                    ) : provider.adapterWired === false ? (
                      // Deliberately not installable: a provider we cannot drive would
                      // sit in the tenant's installed list looking healthy while syncing
                      // nothing.
                      <Button
                        disabled
                        variant="outline"
                        title="Not available yet — tell us you need it and we'll prioritise this provider."
                        className="w-full flex items-center justify-center gap-2"
                      >
                        Coming soon
                      </Button>
                    ) : (
                      <Button
                        onClick={() => openInstall(provider)}
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        Install
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              ))}
            </div>
            </>
          )}
        </div>
      )}

      {/* â”€â”€â”€ Installed Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {activeTab === 'installed' && (
        <div className="space-y-4">
          {installed.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <PuzzlePieceIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No providers installed yet</p>
              <p className="text-sm">Visit the Discover tab to find and install providers</p>
            </div>
          ) : (
            installed.map(prov => (
              <Card key={prov.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <ProviderIcon icon={prov.provider.icon} />
                      <div>
                        <h3 className="font-semibold text-gray-900">{prov.provider.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            variant={
                              prov.syncStatus === 'healthy' ? 'success'
                                : prov.syncStatus === 'failing' ? 'destructive'
                                : 'secondary'
                            }
                          >
                            {prov.syncStatus === 'healthy'
                              ? <CheckCircleIcon className="w-3 h-3 mr-1" />
                              : <ExclamationTriangleIcon className="w-3 h-3 mr-1" />}
                            {prov.syncStatus}
                          </Badge>
                          {!prov.isActive && (
                            <Badge variant="warning">Paused</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Installed {new Date(prov.installedAt).toLocaleDateString()}
                          {prov.lastSyncAt && ` • Last sync: ${new Date(prov.lastSyncAt).toLocaleString()}`}
                        </p>
                        {prov.syncSchedule && (
                          <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                            <CogIcon className="w-3 h-3" /> Schedule: {prov.syncSchedule}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTestConnection(prov.id)}
                        disabled={testing === prov.id}
                        className="flex items-center gap-1"
                      >
                        <PlayIcon className={`w-4 h-4 ${testing === prov.id ? 'animate-spin' : ''}`} />
                        Test
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUninstall(prov.providerId)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* â”€â”€â”€ Install Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {selectedProvider && (
        <Dialog open={!!selectedProvider} onOpenChange={(open) => { if (!open) setSelectedProvider(null) }}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader className="flex-row items-center gap-3 space-y-0">
              <ProviderIcon icon={selectedProvider.icon} />
              <div>
                <DialogTitle>Install {selectedProvider.name}</DialogTitle>
                <DialogDescription>Configure your connection</DialogDescription>
              </div>
            </DialogHeader>

              {/* Config form */}
              {selectedProvider.configSchema?.properties && (
                <div className="space-y-4">
                  {Object.entries(selectedProvider.configSchema.properties).map(([key, prop]: [string, any]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {prop.label || key}
                        {selectedProvider.configSchema.required?.includes(key) && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </label>
                      {prop.enum ? (
                        <Select
                          value={configForm[key] || prop.default || ''}
                          onValueChange={v => setConfigForm(f => ({ ...f, [key]: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={prop.placeholder || 'Select...'} />
                          </SelectTrigger>
                          <SelectContent>
                            {prop.enum.map((v: string) => (
                              <SelectItem key={v} value={v}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type={prop.secret ? 'password' : 'text'}
                          placeholder={prop.placeholder || ''}
                          value={configForm[key] || ''}
                          onChange={e => setConfigForm(f => ({ ...f, [key]: e.target.value }))}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {installError && (
                <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {installError}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <Button variant="ghost" onClick={() => setSelectedProvider(null)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleInstall}
                  disabled={installing}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  {installing ? (
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  )}
                  {installing ? 'Installing...' : 'Install Provider'}
                </Button>
              </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
